import { FC, useState } from 'react';
import { DefaultModalLayout } from '../../../../components/modal/layouts/default-modal-layout';
import FontHeading3 from '../../../../components/fonts/font-heading3/font-heading3';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import { BUTTON_VARIANTS } from '../../../../components/inputs/button-variants';
import useDashboardTranslation from '../../../../dashboard-translation/hooks/dashboard-translation-hook';
import TermsAcceptanceModalContent from './terms-acceptance-modal-content';
import './terms-acceptance-modal.scss';

const TRANSLATION_SCOPE = 'dashboard-onboarding';

export interface ITermsAcceptanceModalBaseProps {
    termsContent: string;
    onAccept: () => Promise<void>;
    onClose?: () => void;
}

const TermsAcceptanceModalBase: FC<ITermsAcceptanceModalBaseProps> = ({
    termsContent,
    onAccept,
    onClose,
}) => {
    const { getTranslation } = useDashboardTranslation();
    const [isChecked, setIsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isClosable = !!onClose;

    const handleClose = () => {
        if (isSubmitting || !onClose) {
            return;
        }

        setIsChecked(false);
        onClose();
    };

    const handleAccept = async () => {
        if (!isChecked || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            await onAccept();
            setIsChecked(false);
            onClose?.();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <DefaultModalLayout
            isCloseDisabled={isSubmitting || !isClosable}
            onCloseClicked={handleClose}
            buttons={[
                {
                    text: getTranslation('TERMS_ACCEPTANCE_ACCEPT_BUTTON', TRANSLATION_SCOPE),
                    variant: BUTTON_VARIANTS.Primary,
                    onClick: handleAccept,
                    disabled: !isChecked || isSubmitting,
                    hasLoader: true,
                    isLoading: isSubmitting,
                },
            ]}
        >
            <div
                className="terms-acceptance-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="terms-acceptance-modal-title"
                tabIndex={-1}
            >
                <div id="terms-acceptance-modal-title" className="terms-acceptance-modal__title">
                    <FontHeading3 color={FONT_COLOR.NEUTRAL11_MENU} variant={[FONT_VARIANT.BOLD]}>
                        {getTranslation('TERMS_ACCEPTANCE_TITLE', TRANSLATION_SCOPE)}
                    </FontHeading3>
                </div>
                <TermsAcceptanceModalContent
                    termsContent={termsContent}
                    isChecked={isChecked}
                    isSubmitting={isSubmitting}
                    onCheckedChange={setIsChecked}
                />
            </div>
        </DefaultModalLayout>
    );
};

export default TermsAcceptanceModalBase;
